import { AgentAvatarPreview } from "@/components/ui/agent-avatar-preview";
import { Icons } from "@/components/ui/icons";
import { cn } from "@/lib/cn";

const agents = [
  { name: "Denker", color: "#30D158" },
  { name: "Claude", color: "#8B5CF6" },
  { name: "Codex", color: "#38BDF8" },
];

const ringOptions = [
  {
    name: "Current ready ring",
    description: "Updated production style: solid green with a softer glow.",
    className: "denker-agent-avatar-ready-ring",
  },
  {
    name: "No dark outer stroke",
    description: "Keeps the green result cue but removes the black outline.",
    className:
      "border-2 border-[#30D158]/95 shadow-[0_0_8px_rgba(48,209,88,0.32)]",
  },
  {
    name: "Soft halo",
    description: "Less border, more notification-like completion glow.",
    className:
      "border border-[#30D158]/85 shadow-[0_0_0_3px_rgba(48,209,88,0.14),0_0_10px_rgba(48,209,88,0.2)]",
  },
  {
    name: "Tiny check badge",
    description: "Removes the full ring and marks completion with a badge.",
    className: "border border-white/20",
    badge: true,
  },
];

function DockAvatar({
  name,
  state,
  ringClassName,
  badge = false,
}: {
  name: string;
  state: "running" | "ready" | "error";
  ringClassName?: string;
  badge?: boolean;
}) {
  return (
    <button
      type="button"
      className="relative h-7 w-7 shrink-0 rounded-full transition-transform hover:z-10 hover:scale-110"
      aria-label={`${name} ${state}`}
      data-testid={`compact-agent-dock-avatar-${name.toLowerCase()}-${state}`}
    >
      {state === "running" && (
        <span
          aria-hidden="true"
          className="denker-pill-runner pointer-events-none absolute -inset-0.5 rounded-full"
        />
      )}
      {state === "ready" && !badge && (
        <span
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute -inset-0.5 rounded-full",
            ringClassName,
          )}
        />
      )}
      <AgentAvatarPreview
        styleKey="glass"
        seed={name}
        size={28}
        className="relative border border-white/20 bg-black/20"
      />
      {state === "ready" && badge && (
        <span
          aria-hidden="true"
          className="absolute -right-0.5 -top-0.5 grid h-2.5 w-2.5 place-items-center rounded-full bg-[#30D158] text-[7px] font-bold leading-none text-[#02140A] ring-1 ring-black/35"
        >
          <Icons.Check className="h-1.5 w-1.5" strokeWidth={4} />
        </span>
      )}
      {state === "error" && (
        <span
          aria-hidden="true"
          className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-red-500 ring-1 ring-black/50"
        />
      )}
    </button>
  );
}

function AvatarStack({
  state,
  ringClassName,
  badge,
}: {
  state: "running" | "ready" | "error";
  ringClassName?: string;
  badge?: boolean;
}) {
  return (
    <div className="flex items-center">
      {agents.map((agent, index) => (
        <div key={agent.name} className={cn(index > 0 && "-ml-1.5")}>
          <DockAvatar
            name={agent.name}
            state={state}
            ringClassName={ringClassName}
            badge={badge}
          />
        </div>
      ))}
    </div>
  );
}

export function CompactAgentDockPage() {
  return (
    <div className="min-h-full bg-[#0A0A0B] px-8 py-10 text-white">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#30D158]">
            Compact Widget
          </p>
          <h1 className="mt-2 text-2xl font-semibold">Agent Dock States</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-white/55">
            The finished-session ring in production is the first ready option
            below. The old dark outer circle has been removed from
            <code className="mx-1 rounded bg-white/10 px-1.5 py-0.5 text-xs text-white/70">
              .denker-agent-avatar-ready-ring
            </code>
            .
          </p>
        </div>

        <section className="mb-8 rounded-xl border border-white/[0.08] bg-white/[0.03] p-5">
          <div className="mb-4 flex items-center justify-between gap-4">
            <div>
              <h2 className="text-sm font-medium text-white/85">
                Production States
              </h2>
              <p className="mt-1 text-xs text-white/40">
                Running uses the rotating green light. Ready uses the static
                completion ring. Error uses the red badge.
              </p>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="rounded-lg border border-white/[0.06] bg-black/20 p-4">
              <div className="mb-3 text-xs text-white/45">Running</div>
              <AvatarStack state="running" />
            </div>
            <div className="rounded-lg border border-white/[0.06] bg-black/20 p-4">
              <div className="mb-3 text-xs text-white/45">Ready</div>
              <AvatarStack
                state="ready"
                ringClassName="denker-agent-avatar-ready-ring"
              />
            </div>
            <div className="rounded-lg border border-white/[0.06] bg-black/20 p-4">
              <div className="mb-3 text-xs text-white/45">Error</div>
              <AvatarStack state="error" />
            </div>
          </div>
        </section>

        <section className="grid gap-4 md:grid-cols-2">
          {ringOptions.map((option) => (
            <div
              key={option.name}
              className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-5"
            >
              <div className="mb-5">
                <h2 className="text-sm font-medium text-white/85">
                  {option.name}
                </h2>
                <p className="mt-1 text-xs leading-5 text-white/42">
                  {option.description}
                </p>
              </div>
              <div className="flex items-center justify-between gap-6">
                <AvatarStack
                  state="ready"
                  ringClassName={option.className}
                  badge={option.badge}
                />
                <div className="flex h-14 w-32 items-center justify-center rounded-lg bg-[#18181B]">
                  <AvatarStack
                    state="ready"
                    ringClassName={option.className}
                    badge={option.badge}
                  />
                </div>
              </div>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}
